import * as d3 from 'd3';
import _ from 'lodash';
import * as graphUtils from './graphUtils';

/**
 * Adds right-click functionality for outlier display across chart components
 * Uses the proven SpectrumChart pattern as the foundation
 *
 * @param {Object} ctx - Chart context from LineChart HOC
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableRightClick - Whether right-click modal should be enabled
 * @param {boolean} options.miniMode - Whether chart is in mini mode
 * @param {Function} options.onRightClick - Callback when right-click occurs
 * @param {string} options.component - Component type ('spectrum' or 'trend')
 * @returns {Function|null} - The right-click handler or null if not enabled
 */
export const addOutlierRightClickEvent = (ctx, options = {}) => {
  const {
    enableRightClick = true,
    miniMode = false,
    onRightClick,
    component = 'spectrum'
  } = options;

  // Check if right-click already exists to prevent multiple attachments
  if (ctx.rightClick) return ctx.rightClick;

  // Validate required parameters
  if (!ctx || !ctx.chart || !onRightClick) {
    // Invalid context or missing callback
    return null;
  }

  try {
    // Enable right-click for all trend charts, including mini trend charts
    // Preserve previous behavior for non-trend components
    const shouldEnableRightClick = enableRightClick &&
      (component === 'trend' || !miniMode);

    if (!shouldEnableRightClick) return null;

    // Use the proven SpectrumChart pattern
    const rightClickHandler = graphUtils.addRightMouseClickEvent(ctx.chart, () => {
      try {
        const selection = ctx.props.selection;
        if (!selection) {
          // No selection available
          return;
        }

        // Handle both single selection and array selection
        const xValue = _.isArray(selection) ? selection[0].x : selection.x;
        const yValue = _.isArray(selection) ? selection[0].y : selection.y;

        if (xValue === undefined || yValue === undefined) {
          // Invalid selection data
          return;
        }

        const dataPos = {
          x: ctx.x(xValue) - 60, // '60' to compensate for ChartPopupContainer offset
          y: ctx.y(yValue),
          // Only add alignLeft for trend charts to maintain existing behavior
          ...(component === 'trend' && {
            alignLeft: ctx.x(xValue) > ctx.chart.getBoundingClientRect().width - 250
          })
        };

        // Call the provided callback with position and selection data
        onRightClick(dataPos, selection);
      } catch (error) {
        // Silent fail for production stability
      }
    });

    // Store reference for cleanup
    ctx.rightClick = rightClickHandler;
    return rightClickHandler;
  } catch (error) {
    // Silent fail for production stability
    return null;
  }
};

/**
 * Properly cleans up right-click event listeners to prevent memory leaks
 *
 * @param {Object} ctx - Chart context from LineChart HOC
 */
export const cleanupRightClickEvent = (ctx) => {
  if (!ctx) return;

  try {
    if (ctx.rightClick && ctx.chart) {
      // Properly remove D3 event listener
      d3.select(ctx.chart).on('contextmenu', null);
      ctx.rightClick = null;
    }
  } catch (error) {
    // Silent fail for cleanup
  }
};
